import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";

type PetWeightRow = Database["public"]["Tables"]["pet_weights"]["Row"];
type PetWeightInsertBase = Database["public"]["Tables"]["pet_weights"]["Insert"];
type PetWeightUpdateBase = Database["public"]["Tables"]["pet_weights"]["Update"];

export type PetWeightSource = "manual" | "registration" | "profile_update" | "consultation";

type PetWeightInsert = PetWeightInsertBase & {
  source?: PetWeightSource;
  medical_record_id?: string | null;
};

type PetWeightUpdate = PetWeightUpdateBase & {
  source?: PetWeightSource;
  medical_record_id?: string | null;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) return String(error.message);
  return "Erro inesperado";
};

export const useCreatePetWeight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      petId,
      weight,
      measuredAt,
      source = "manual",
    }: {
      petId: string;
      weight: number;
      measuredAt?: string | null;
      source?: PetWeightSource;
    }) => {
      const payload: PetWeightInsert = {
        pet_id: petId,
        weight,
        measured_at: measuredAt ?? undefined,
        source,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("pet_weights")
        .insert(payload)
        .select("*")
        .single();
      if (error) throw error;
      return data as PetWeightRow;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["pet", created.pet_id] });
      invalidatePatientsCache(queryClient, { petIds: [created.pet_id] });
      toast.success("Peso registrado com sucesso!");
    },
    onError: (error: unknown) => {
      toast.error("Erro ao registrar peso", { description: getErrorMessage(error) });
    },
  });
};

export const useUpdatePetWeight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      petId,
      patch,
    }: {
      id: string;
      petId: string;
      patch: Pick<PetWeightUpdate, "weight" | "measured_at">;
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("pet_weights")
        .update({
          ...patch,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return { petId, row: data as PetWeightRow };
    },
    onSuccess: ({ petId }) => {
      queryClient.invalidateQueries({ queryKey: ["pet", petId] });
      invalidatePatientsCache(queryClient, { petIds: [petId] });
      toast.success("Peso atualizado com sucesso!");
    },
    onError: (error: unknown) => {
      toast.error("Erro ao atualizar peso", { description: getErrorMessage(error) });
    },
  });
};

export const useDeletePetWeight = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, petId }: { id: string; petId: string }) => {
      const deletedAt = new Date().toISOString();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("pet_weights")
        .update({ deleted_at: deletedAt, updated_at: deletedAt })
        .eq("id", id);
      if (error) throw error;
      return { petId };
    },
    onSuccess: ({ petId }) => {
      queryClient.invalidateQueries({ queryKey: ["pet", petId] });
      invalidatePatientsCache(queryClient, { petIds: [petId] });
      toast.success("Peso removido com sucesso!");
    },
    onError: (error: unknown) => {
      toast.error("Erro ao remover peso", { description: getErrorMessage(error) });
    },
  });
};

